/**
 * Firestore Database Service
 *
 * Provides database connectivity and operations for Zwizle analytics
 * using Firestore for document storage
 */

import {
  getFirestore,
  collection,
  doc,
  setDoc,
  addDoc,
  query,
  where,
  orderBy,
  limit,
  getDocs,
  WhereFilterOp,
} from 'firebase/firestore'
import { FirebaseApp } from 'firebase/app'
import { app } from './firebase'

// Define types for document data
type DocData = Record<string, string | number | boolean | null | object>

// Initialize Firestore
const db = getFirestore(app as FirebaseApp)

/**
 * Insert a document into a collection
 *
 * @param collectionName - Collection name
 * @param data - Document data to insert
 * @param docId - Optional document ID (will be auto-generated if not provided)
 * @returns Promise resolving to document ID
 */
export const insertDocument = async (
  collectionName: string,
  data: DocData,
  docId?: string
): Promise<string> => {
  try {
    if (docId) {
      // Use specified document ID
      const docRef = doc(db, collectionName, docId)
      await setDoc(docRef, {
        ...data,
        created_at: data.created_at || new Date().toISOString(),
      })
      return docId
    } else {
      // Auto-generate document ID
      const collectionRef = collection(db, collectionName)
      const docRef = await addDoc(collectionRef, {
        ...data,
        created_at: data.created_at || new Date().toISOString(),
      })
      return docRef.id
    }
  } catch (error) {
    console.error(`Error inserting document into ${collectionName}:`, error)
    throw error
  }
}

/**
 * Update a document in a collection
 *
 * @param collectionName - Collection name
 * @param docId - Document ID to update
 * @param data - Data to update
 * @returns Promise resolving when update completes
 */
export const updateDocument = async (
  collectionName: string,
  docId: string,
  data: DocData
): Promise<void> => {
  try {
    const docRef = doc(db, collectionName, docId)
    await setDoc(docRef, data, { merge: true })
  } catch (error) {
    console.error(
      `Error updating document ${docId} in ${collectionName}:`,
      error
    )
    throw error
  }
}

/**
 * Query documents from a collection with filtering
 *
 * @param collectionName - Collection name
 * @param filters - Filter conditions as array of [field, operator, value]
 * @param limitCount - Optional maximum number of documents to return
 * @param orderByField - Optional field to order results by
 * @param orderDirection - Optional direction to order results
 * @returns Promise resolving to array of documents
 */
export const queryDocuments = async (
  collectionName: string,
  filters: [string, WhereFilterOp, unknown][],
  limitCount?: number,
  orderByField?: string,
  orderDirection?: 'asc' | 'desc'
): Promise<DocData[]> => {
  try {
    const collectionRef = collection(db, collectionName)

    // Build query with filters
    let queryRef = query(collectionRef)

    // Add filters
    filters.forEach(([field, op, value]) => {
      queryRef = query(queryRef, where(field, op, value))
    })

    // Add ordering if specified
    if (orderByField) {
      queryRef = query(queryRef, orderBy(orderByField, orderDirection || 'asc'))
    }

    // Add limit if specified
    if (limitCount) {
      queryRef = query(queryRef, limit(limitCount))
    }

    // Execute query
    const querySnapshot = await getDocs(queryRef)

    // Process results
    const results: DocData[] = []
    querySnapshot.forEach((doc) => {
      results.push({
        id: doc.id,
        ...(doc.data() as DocData),
      })
    })

    return results
  } catch (error) {
    console.error(`Error querying documents from ${collectionName}:`, error)
    throw error
  }
}

export default {
  insertDocument,
  updateDocument,
  queryDocuments,
}
